/* Vendor 360 Profile — Supplier Intelligence Graph view.
 *
 * Endpoint: GET /api/vendor-intelligence/vendor360/:id
 *           GET /api/vendor-intelligence/matrix?supplier_id=:id
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  ArrowLeft, Truck, ShieldCheck, AlertTriangle, TrendingUp, TrendingDown,
  Activity, DollarSign, Package, Award, Sparkles, RefreshCw, Star,
  ArrowUpRight, ArrowDownRight, Minus,
} from 'lucide-react';


const tierStyle = {
  preferred: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  approved:  'bg-blue-100 text-blue-800 border-blue-300',
  spot:      'bg-amber-100 text-amber-800 border-amber-300',
  blocked:   'bg-rose-100 text-rose-800 border-rose-300',
};

const trendIcon = {
  up:   <ArrowUpRight className="w-3 h-3 text-rose-600" />,
  down: <ArrowDownRight className="w-3 h-3 text-emerald-600" />,
  flat: <Minus className="w-3 h-3 text-gray-400" />,
};


function ScoreBar({ label, value, color }) {
  const pct = Math.max(0, Math.min(100, value || 0));
  return (
    <div className="space-y-1" data-testid={`v360-score-${label.toLowerCase()}`}>
      <div className="flex justify-between text-xs">
        <span className="font-semibold text-gray-700">{label}</span>
        <span className="font-mono">{pct.toFixed(1)}</span>
      </div>
      <div className="w-full bg-gray-100 rounded-full h-2">
        <div className="h-2 rounded-full transition-all"
              style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
    </div>
  );
}


export default function Vendor360Profile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [matrix, setMatrix]   = useState(null);

  const refresh = useCallback(async () => {
    try {
      const [p, m] = await Promise.all([
        axiosInstance.get(`/vendor-intelligence/vendor360/${id}`),
        axiosInstance.get(`/vendor-intelligence/matrix?supplier_id=${id}&limit=200`),
      ]);
      setProfile(p.data);
      setMatrix(m.data);
    } catch (_) { toast.error('Failed to load Vendor 360'); }
  }, [id]);

  useEffect(() => { refresh(); }, [refresh]);

  if (!profile) {
    return <div className="p-6 text-sm text-gray-500" data-testid="v360-loading">Loading Vendor 360…</div>;
  }

  return (
    <div className="p-6 space-y-4" data-testid="vendor-360-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div>
          <button onClick={() => navigate('/suppliers')}
                  className="text-xs text-gray-500 hover:text-gray-700 inline-flex items-center gap-1 mb-1">
            <ArrowLeft className="w-3 h-3" /> Back to Suppliers
          </button>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Truck className="w-6 h-6 text-indigo-600" />
            {profile.supplier_name || profile.supplier_id}
            <span className={`text-xs px-2 py-0.5 rounded font-semibold border ${tierStyle[profile.tier] || tierStyle.spot}`}
                  data-testid="v360-tier">
              {profile.tier?.toUpperCase()}
            </span>
          </h1>
          <div className="text-xs text-gray-500 mt-1 flex gap-3">
            {profile.supplier_code && <span>Code: <span className="font-mono">{profile.supplier_code}</span></span>}
            {profile.vat_no && <span>VAT: <span className="font-mono">{profile.vat_no}</span></span>}
            {profile.email && <span>{profile.email}</span>}
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => navigate(`/suppliers/${id}/price-list`)}
                  className="px-3 py-1.5 rounded text-xs border border-gray-300 hover:bg-gray-50 inline-flex items-center gap-1">
            <DollarSign className="w-3 h-3" /> Price List
          </button>
          <button onClick={refresh}
                  className="px-3 py-1.5 rounded text-xs border border-gray-300 hover:bg-gray-50 inline-flex items-center gap-1">
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>
      </div>

      {/* Final Score hero */}
      <div className="border border-indigo-200 bg-gradient-to-r from-indigo-50 to-violet-50 rounded-lg p-4 flex items-center justify-between"
           data-testid="v360-final-score-card">
        <div>
          <div className="text-xs uppercase tracking-wider text-indigo-700 inline-flex items-center gap-1">
            <Award className="w-3 h-3" /> Final Score
          </div>
          <div className="text-5xl font-bold text-indigo-700 mt-1 font-mono"
               data-testid="v360-final-score">
            {profile.final_score?.toFixed(1)}<span className="text-xl text-indigo-400">/100</span>
          </div>
          <div className="text-xs text-gray-600 mt-1">
            Weighted: {Math.round((profile.weights?.price || 0) * 100)}% price + {Math.round((profile.weights?.delivery || 0) * 100)}% delivery + {Math.round((profile.weights?.quality || 0) * 100)}% quality + {Math.round((profile.weights?.risk || 0) * 100)}% risk
          </div>
        </div>
        <div className="flex-1 max-w-md space-y-2 ml-6">
          <ScoreBar label="Price"    value={profile.price_score}    color="#6366f1" />
          <ScoreBar label="Delivery" value={profile.delivery_score} color="#10b981" />
          <ScoreBar label="Quality"  value={profile.quality_score}  color="#f59e0b" />
          <ScoreBar label="Risk"     value={profile.risk_score}     color="#ef4444" />
        </div>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard icon={Package} label="Items Covered" value={profile.item_count}
                  hint={`across ${profile.category_coverage?.length || 0} categor${profile.category_coverage?.length === 1 ? 'y' : 'ies'}`} />
        <KpiCard icon={Activity} label="RFQ Quotes" value={profile.activity?.rfq_quotes}
                  hint={`win rate ${profile.activity?.win_rate_pct ?? 0}%`} />
        <KpiCard icon={ShieldCheck} label="POs Received" value={`${profile.activity?.fully_received || 0} / ${profile.activity?.po_count || 0}`}
                  hint={`fully received`} />
        <KpiCard icon={DollarSign} label="Total Spend"
                  value={(profile.spend?.total_spend || 0).toLocaleString()}
                  hint={`SAR · ${profile.spend?.posted_bills || 0} bills posted`} />
      </div>

      {/* Categories */}
      {profile.category_coverage?.length > 0 && (
        <div className="border border-gray-200 rounded-lg p-3 bg-white">
          <div className="text-xs uppercase tracking-wider text-gray-500 mb-2 inline-flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> Category Coverage
          </div>
          <div className="flex flex-wrap gap-1.5">
            {profile.category_coverage.map((c) => (
              <span key={c} className="px-2 py-0.5 rounded bg-indigo-50 text-indigo-700 text-xs font-medium border border-indigo-200"
                    data-testid={`v360-cat-${c}`}>{c}</span>
            ))}
          </div>
        </div>
      )}

      {/* Vendor × Item Matrix */}
      <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
        <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 font-semibold text-sm flex items-center justify-between">
          <span className="inline-flex items-center gap-2">
            <Package className="w-4 h-4" /> Vendor × Item Matrix
            <span className="text-xs font-mono px-1.5 py-0.5 rounded bg-gray-900 text-white">{matrix?.row_count || 0}</span>
          </span>
          <span className="text-[10px] text-gray-500">
            Auto-derived from vendor_prices · sorted by most recently quoted
          </span>
        </div>
        <table className="w-full text-sm" data-testid="v360-matrix-table">
          <thead className="bg-gray-50 text-xs text-gray-600 border-b border-gray-200">
            <tr>
              <th className="text-left px-3 py-2">Item Code</th>
              <th className="text-left px-3 py-2">Description</th>
              <th className="text-right px-3 py-2">Last Price</th>
              <th className="text-right px-3 py-2">Avg</th>
              <th className="text-right px-3 py-2">Min – Max</th>
              <th className="text-center px-3 py-2">Trend</th>
              <th className="text-right px-3 py-2">Quotes</th>
              <th className="text-left px-3 py-2">Last Seen</th>
            </tr>
          </thead>
          <tbody>
            {(matrix?.relationships || []).length === 0 && (
              <tr><td colSpan="8" className="px-3 py-8 text-center text-sm text-gray-500">
                No items recorded for this supplier yet.
              </td></tr>
            )}
            {(matrix?.relationships || []).map((row) => (
              <tr key={`${row.supplier_id}-${row.item_code}`}
                  className="border-t border-gray-100 hover:bg-indigo-50/30 cursor-pointer"
                  onClick={() => navigate(`/item-master`)}
                  data-testid={`v360-matrix-row-${row.item_code}`}>
                <td className="px-3 py-2 font-mono text-xs">{row.item_code}</td>
                <td className="px-3 py-2 text-sm">{row.item_description}</td>
                <td className="px-3 py-2 text-right font-mono font-semibold text-indigo-700">
                  {Number(row.last_price).toLocaleString()}
                </td>
                <td className="px-3 py-2 text-right font-mono text-xs">
                  {Number(row.average_price).toLocaleString()}
                </td>
                <td className="px-3 py-2 text-right font-mono text-xs text-gray-500">
                  {row.min_price} – {row.max_price}
                </td>
                <td className="px-3 py-2 text-center">
                  <span className="inline-flex items-center gap-1 text-xs">
                    {trendIcon[row.trend] || trendIcon.flat}
                    <span className={
                      row.trend === 'up' ? 'text-rose-700' :
                      row.trend === 'down' ? 'text-emerald-700' : 'text-gray-500'
                    }>{row.trend_pct >= 0 ? '+' : ''}{row.trend_pct}%</span>
                  </span>
                </td>
                <td className="px-3 py-2 text-right font-mono text-xs">{row.price_count}</td>
                <td className="px-3 py-2 text-xs text-gray-500">{row.last_seen?.slice(0, 10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="text-[11px] text-gray-400">
        Computed at {profile.computed_at?.slice(0, 16).replace('T', ' ')}
      </div>
    </div>
  );
}


function KpiCard({ icon: Icon, label, value, hint }) {
  return (
    <div className="border border-gray-200 rounded-lg px-3 py-2 bg-white"
         data-testid={`v360-kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="text-[10px] uppercase tracking-wider text-gray-500 inline-flex items-center gap-1">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="text-lg font-bold mt-0.5">{value ?? '—'}</div>
      {hint && <div className="text-[10px] text-gray-500 mt-0.5">{hint}</div>}
    </div>
  );
}
